<template>
  <v-form v-model="valid">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-menu
            :close-on-content-click="false"
            offset-y
            transition="scale-transition"
            v-model="menuFromDate"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :rules="fromDateRule"
                @blur="fromDate = parseDate(fromDateFormatted)"
                :label="$t('fromDate')"
                outlined
                persistent-hint
                readonly
                v-model="fromDateFormatted"
                v-on="on"
              />
            </template>
            <v-date-picker
              @input="menuFromDate = false"
              header-color="#111111"
              v-model="fromDate"
              @change="onItemChange"
            />
          </v-menu>
        </v-col>
        <v-col>
          <v-menu
            :close-on-content-click="false"
            offset-y
            transition="scale-transition"
            v-model="menuToDate"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :rules="toDateRule"
                @blur="toDate = parseDate(toDateFormatted)"
                :label="$t('toDate')"
                outlined
                persistent-hint
                readonly
                v-model="toDateFormatted"
                v-on="on"
              />
            </template>
            <v-date-picker
              @input="menuToDate = false"
              header-color="#111111"
              v-model="toDate"
              @change="onItemChange"
            />
          </v-menu>
        </v-col>
        <v-col>
          <v-select
            v-model="selectedBrokers"
            :items="brokerAccounts"
            :label="$t('brokerAccount')"
            item-value="id"
            multiple
            outlined
            @change="onItemChange"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleFilters.brokers"
              >
                <v-icon
                  :color="selectedBrokers.length > 0 ? 'green lighten-1' : ''"
                  small
                  >{{ allBrokers ? checkIcon : circleIcon }}</v-icon
                >
                <v-list-item-title class="ml-2">
                  {{ $t("all_OverallFilters") }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:item="data">
              <v-icon
                v-if="selectedBrokers.includes(data.item.id)"
                class="mr-2"
                small
                >{{ checkIcon }}</v-icon
              >
              <v-icon v-else class="mr-2" small>{{ circleIcon }}</v-icon>
              {{ `${data.item.broker.name} · ${data.item.identifier}` }}
              <img
                v-if="data.item.verified"
                src="@/assets/icons/Audited.svg"
                alt="Audited Icon"
                class="ml-2"
                style="width: 16px; height: 16px; color: blue"
              />
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0 && allBrokers">{{
                $t("all_OverallFilters")
              }}</span>
              <div v-else-if="!allBrokers">
                <v-chip v-if="index === 0"
                  ><span>{{ item.identifier }}</span></v-chip
                >
                <span v-if="index === 1" class="grey--text text-caption">
                  (+{{ selectedBrokers.length - 1 }} others)
                </span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col>
          <v-select
            :items="sides"
            class="mb-n3"
            :label="$t('side')"
            outlined
            v-model="sideSelected"
            @change="onItemChange"
          >
            <template v-slot:selection="data">
              {{ data.item === "All" ? $t("all_OverallFilters") : data.item }}
            </template>
            <template v-slot:item="data">
              {{ data.item === "All" ? $t("all_OverallFilters") : data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col>
          <v-select
            :items="results"
            class="mb-n3"
            :label="$t('result')"
            outlined
            v-model="resultSelected"
            @change="onItemChange"
          >
            <template v-slot:selection="data">
              {{
                data.item === "All"
                  ? $t("all_OverallFilters")
                  : data.item === "Losing"
                  ? $t("negativeResult")
                  : $t("positiveResult")
              }}
            </template>
            <template v-slot:item="data">
              {{
                data.item === "All"
                  ? $t("all_OverallFilters")
                  : data.item === "Losing"
                  ? $t("negativeResult")
                  : $t("positiveResult")
              }}
            </template>
          </v-select>
        </v-col>
        <v-col>
          <v-select
            v-model="selectedAssets"
            :items="assets"
            :label="$t('asset')"
            item-value="ticker"
            multiple
            outlined
            @change="onItemChange"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleFilters.assets"
              >
                <v-icon
                  :color="selectedAssets.length > 0 ? 'green lighten-1' : ''"
                  small
                  >{{ allAssets ? checkIcon : circleIcon }}</v-icon
                >
                <v-list-item-title class="ml-2">
                  {{ $t("all_OverallFilters") }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:item="data">
              <v-icon
                v-if="selectedAssets.includes(data.item.ticker)"
                class="mr-2"
                small
                >{{ checkIcon }}</v-icon
              >
              <v-icon v-else class="mr-2" small>{{ circleIcon }}</v-icon>
              {{ data.item.ticker }}
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0 && allAssets">{{
                $t("all_OverallFilters")
              }}</span>
              <div v-else-if="!allAssets">
                <v-chip v-if="index === 0"
                  ><span>{{ item.ticker }}</span></v-chip
                >
                <span v-if="index === 1" class="grey--text text-caption">
                  (+{{ selectedAssets.length - 1 }} others)
                </span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col>
          <v-select
            v-model="selectedMoods"
            :items="moods"
            :label="$t('mood')"
            multiple
            outlined
            @change="onItemChange"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleFilters.moods"
              >
                <v-icon
                  :color="selectedMoods.length > 0 ? 'green lighten-1' : ''"
                  class="mr-2"
                  small
                  >{{ allMoods ? checkIcon : circleIcon }}</v-icon
                >
                <v-list-item-title>
                  {{ $t("all_OverallFilters") }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:item="data">
              <v-icon
                v-if="selectedMoods.includes(data.item)"
                class="mr-2"
                small
                >{{ checkIcon }}</v-icon
              >
              <v-icon v-else class="mr-2" small>{{ circleIcon }}</v-icon>
              {{ data.item }}
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0 && allMoods">{{
                $t("all_OverallFilters")
              }}</span>
              <div v-else-if="!allMoods">
                <v-chip v-if="index === 0"
                  ><span>{{ item }}</span></v-chip
                >
                <span v-if="index === 1" class="grey--text text-caption">
                  (+{{ selectedMoods.length - 1 }} others)
                </span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col>
          <v-select
            v-model="selectedStrategies"
            :items="strategies"
            :label="strategiesLabel"
            item-value="id"
            multiple
            outlined
            @change="onItemChange"
          >
            <template v-slot:prepend-item
              >s
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleFilters.strategies"
              >
                <v-icon
                  :color="
                    selectedStrategies.length > 0 ? 'green lighten-1' : ''
                  "
                  small
                  >{{ allStrategies ? checkIcon : circleIcon }}</v-icon
                >
                <v-list-item-title class="ml-2">
                  {{ $t("all_OverallFilters") }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:item="data">
              <v-icon
                v-if="selectedStrategies.includes(data.item.id)"
                class="mr-2"
                small
                >{{ checkIcon }}</v-icon
              >
              <v-icon v-else class="mr-2" small>{{ circleIcon }}</v-icon>
              {{ data.item.name }}
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0 && allStrategies">{{
                $t("all_OverallFilters")
              }}</span>
              <div v-else-if="!allStrategies">
                <v-chip v-if="index === 0"
                  ><span>{{ item.name }}</span></v-chip
                >
                <span v-if="index === 1" class="grey--text text-caption">
                  (+{{ selectedStrategies.length - 1 }} others)
                </span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col class="d-flex">
          <v-btn
            class="mt-2"
            :color="filterChanged ? '#00F784' : '#F8F8F8'"
            @click="valuesChanged()"
            >{{ $t("applyFilter") }}</v-btn
          >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mt-2 ml-2"
                v-bind="attrs"
                v-on="on"
                @click="setSelectedItems(true)"
                ><v-icon small>fa-regular fa-rotate-left</v-icon></v-btn
              >
            </template>
            <span>{{ $t("resetTooltip") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import Moods from "../moods";
import Repository from "../../../repositories/journal";
import AssetRepository from "../../../repositories/asset";
import BrokerAccountRepository from "../../../repositories/brokerAccount";
import StrategyRepository from "../../../repositories/strategy";
import { mapMutations } from "vuex";

export default {
  components: {
    // vueMultiSelect
  },
  name: "OverallFilters",
  props: {
    externalUser: Boolean,
    externalUserId: Number,
  },
  data() {
    return {
      // Apply Button Logic
      lastFilter: {},
      filterChanged: false,

      // Selects and Filters
      sides: ["All"],
      results: ["All"],
      moods: [],
      brokerAccounts: [],
      assets: [],
      strategies: [],

      fromDate: new Date().toISOString().substr(0, 10),
      toDate: new Date().toISOString().substr(0, 10),
      fromDateFormatted: this.formatDate(
        new Date().toISOString().substr(0, 10)
      ),
      toDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
      menuFromDate: false,
      menuToDate: false,

      selectedMoods: [],
      selectedBrokers: [],
      selectedAssets: [],
      selectedStrategies: [],
      sideSelected: "",
      resultSelected: "",
      tradeTimeAvg: 0,
      checkIcon: "fa-check-circle",
      circleIcon: "far fa-circle",
      //Rules
      valid: false,
      fromDateRule: [
        // eslint-disable-next-line no-unused-vars
        (f) =>
          this.fromDate <= this.toDate ||
          "From date must be before the last date",
      ],
      toDateRule: [
        // eslint-disable-next-line no-unused-vars
        (f) =>
          this.fromDate <= this.toDate ||
          "From date must be before the last date",
      ],
      toggleFilters: {
        moods: () => {
          this.selectedMoods = this.allMoods ? [] : this.moods.slice();
        },
        brokers: () => {
          this.selectedBrokers = this.allBrokers
            ? []
            : this.brokerAccounts.slice().map((ba) => ba.id);
        },
        assets: () => {
          this.selectedAssets = this.allAssets
            ? []
            : this.assets.slice().map((asset) => asset.ticker);
        },
        strategies: () => {
          this.selectedStrategies = this.allStrategies
            ? []
            : this.strategies.slice().map((stra) => stra.id);
        },
      },
    };
  },
  computed: {
    allMoods() {
      return this.selectedMoods.length === this.moods.length;
    },
    allBrokers() {
      return this.selectedBrokers.length === this.brokerAccounts.length;
    },
    allAssets() {
      return this.selectedAssets.length === this.assets.length;
    },
    allStrategies() {
      return this.selectedStrategies.length === this.strategies.length;
    },
    strategiesLabel() {
      return this.strategies.length === 0
        ? this.$t("all_OverallFilters")
        : this.$t("strategy");
    },
  },
  watch: {
    fromDate() {
      this.fromDateFormatted = this.formatDate(this.fromDate);
    },
    toDate() {
      this.toDateFormatted = this.formatDate(this.toDate);
    },
    "$store.state.book.pageable": function () {
      let filter = this.$store.state.book.currentFilter;
      if (this.externalUser) {
        filter = this.$store.state.book.currentFilterExtUser;
      }
      this.callFilter(filter);
    },
  },
  async mounted() {
    let currentPath = this.$route.name;
    await this.setItems(); // To set filter items
    if (this.externalUser)
      this.setSelectedItems(currentPath === "shared-journal");
    else if (!this.$store.state.book.noTrades)
      this.setSelectedItems(currentPath === "book-home");
  },
  methods: {
    ...mapMutations(["setNoTrades", "setLoadingTrades"]),
    async setItems() {
      await this.$store.state.sides.forEach((side) => {
        this.sides.push(side);
      });
      await this.$store.state.results.forEach((result) => {
        this.results.push(result);
      });
      Moods.forEach((mood) => {
        if (this.$i18n.locale === "en") {
          this.moods.push(mood.en);
        } else {
          this.moods.push(mood.es);
        }
      });
      if (!this.externalUser) {
        // The current user
        await this.$store.state.user.brokerAccounts.forEach((brokerAccount) => {
          this.brokerAccounts.push(brokerAccount);
        });
        await this.$store.state.user.assets.forEach((asset) => {
          this.assets.push(asset);
        });
        await this.$store.state.user.strategies.forEach((strategy) => {
          this.strategies.push(strategy);
        });
        if (this.brokerAccounts.length === 0 || this.assets.length === 0)
          this.setLoadingTrades(false);
        this.setNoTrades(this.brokerAccounts.length === 0);
      } else {
        if (
          this.$store.state.extUser == null ||
          this.$store.state.extUser.id !== this.externalUserId
        ) {
          // if we do not have external user info in the state
          await this.setExternalUserItems(); // save external user info (brokers, assets and strategies)
        } else {
          this.assets = this.$store.state.extUser.assets;
          this.strategies = this.$store.state.extUser.strategies;
          this.brokerAccounts = this.$store.state.extUser.brokerAccounts;
        }
      }
    },
    ...mapMutations(["setExtUser"]),
    async setExternalUserItems() {
      await BrokerAccountRepository.allBrokerAccountsOfExternalUser(
        this.externalUserId
      ).then((response) => {
        if (response.status < 400)
          response.data.forEach((brokerAccount) => {
            this.brokerAccounts.push(brokerAccount);
          });
      });
      await AssetRepository.allAssetsOfExternalUser(this.externalUserId).then(
        (response) => {
          if (response.status < 400)
            response.data.forEach((asset) => {
              this.assets.push(asset);
            });
        }
      );
      await StrategyRepository.allStrategiesOfExternalUser(
        this.externalUserId
      ).then((response) => {
        if (response.status < 400)
          response.data.forEach((strategy) => {
            this.strategies.push(strategy);
          });
      });
      let extUser = {
        id: this.externalUserId,
        assets: this.assets,
        strategies: this.strategies,
        brokerAccounts: this.brokerAccounts,
      };
      this.setExtUser(extUser);
    },
    ...mapMutations([
      "setFilterMade",
      "setCurrentFilterExtUser",
      "setCurrentFilter",
    ]),
    setSelectedItems(allItems) {
      let currentFilter = this.externalUser
        ? this.$store.state.book.currentFilterExtUser
        : this.$store.state.book.currentFilter;
      if (allItems) {
        // Set the filter from 1 month ago till now
        let now = new Date();
        this.toDate = now.toISOString().substr(0, 10);
        this.toDateFormatted = this.formatDate(this.toDate);
        now.setDate(now.getDate() - 30);
        this.fromDate = now.toISOString().substr(0, 10);
        this.fromDateFormatted = this.formatDate(this.fromDate);
        this.sideSelected = "All";
        this.resultSelected = "All";
        this.selectedMoods = this.moods.slice();
        this.selectedBrokers = this.brokerAccounts.slice().map((ba) => ba.id);
        this.selectedAssets = this.assets.slice().map((asset) => asset.ticker);
        this.selectedStrategies = this.strategies
          .slice()
          .map((stra) => stra.id);
        currentFilter.fromDate = now;
        currentFilter.toDate = new Date();
        currentFilter.allStrategies = true;
        currentFilter.strategy = this.selectedStrategies;
        currentFilter.asset = this.selectedAssets;
        currentFilter.brokerAccount = this.selectedBrokers;
        currentFilter.side = "All";
        currentFilter.result = "All";
        currentFilter.mood = Moods.map((m) => m.key);
        this.setFilterMade(false);
        if (this.externalUser) {
          currentFilter.userId = this.externalUserId;
          this.setCurrentFilterExtUser(currentFilter);
        } else {
          this.setCurrentFilter(currentFilter);
        }
      } else {
        let currentSide = currentFilter.side.toLowerCase();
        this.sideSelected =
          currentSide.charAt(0).toUpperCase() + currentSide.slice(1);
        this.resultSelected = currentFilter.result;

        let currentMoods = [];
        for (let i = 0; i < currentFilter.mood.length; i++) {
          if (this.$i18n.locale === "es") {
            currentMoods[i] = Moods.filter(
              (mood) => mood.key === currentFilter.mood[i]
            )[0].es;
          } else {
            currentMoods[i] = Moods.filter(
              (mood) => mood.key === currentFilter.mood[i]
            )[0].en;
          }
        }
        this.selectedMoods = currentMoods;
        this.selectedBrokers = currentFilter.brokerAccount.slice();
        this.selectedAssets = currentFilter.asset.slice();
        this.selectedStrategies =
          currentFilter.strategy.length === this.strategies.length
            ? this.strategies.slice().map((stra) => stra.id)
            : currentFilter.strategy.slice();
        this.fromDate = new Date(currentFilter.fromDate)
          .toISOString()
          .substr(0, 10);
        this.toDate = new Date(currentFilter.toDate)
          .toISOString()
          .substr(0, 10);
      }
      this.callFilter(currentFilter);
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [month, day, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    ...mapMutations([
      "setCurrentFilter",
      "setCurrentFilterExtUser",
      "setFilterMade",
    ]),
    valuesChanged() {
      this.setFilterMade(true);
      if (this.valid) {
        /* eslint-disable no-console */
        let fromDataFiltered = new Date(
          this.fromDate.substr(0, 4),
          this.fromDate.substr(5, 2) - 1,
          this.fromDate.substr(8, 2),
          0,
          0
        );
        let toDateFiltered = new Date(
          this.toDate.substr(0, 4),
          this.toDate.substr(5, 2) - 1,
          this.toDate.substr(8, 2),
          0,
          0
        );
        toDateFiltered.setDate(toDateFiltered.getDate() + 1);
        let moodsKey = [];
        for (let i = 0; i < this.selectedMoods.length; i++) {
          if (this.$i18n.locale === "es") {
            moodsKey[i] = Moods.filter(
              (mood) => mood.es === this.selectedMoods[i]
            )[0].key;
          } else {
            moodsKey[i] = Moods.filter(
              (mood) => mood.en === this.selectedMoods[i]
            )[0].key;
          }
        }

        let filter = {
          fromDate: fromDataFiltered,
          toDate: toDateFiltered,
          side:
            this.sideSelected !== "All"
              ? this.sideSelected.toUpperCase()
              : this.sideSelected,
          mood: moodsKey,
          asset: this.selectedAssets,
          brokerAccount: this.selectedBrokers,
          result: this.resultSelected,
          strategy: this.selectedStrategies,
          allStrategies: this.allStrategies,
        };

        if (this.externalUser) {
          filter.userId = this.externalUserId;
          this.setCurrentFilterExtUser(filter);
        } else {
          this.setCurrentFilter(filter);
        }
        this.callFilter(filter);
      }
    },
    ...mapMutations([
      "setLoadingTrades",
      "setTrades",
      "setStats",
      "setNoTrades",
      "setTradesPageInfo",
    ]),
    async callFilter(filter) {
      if (this.strategies.length === 0 || filter.strategy.length === 0) {
        filter.strategy = ["-1"];
        filter.allStrategies = true;
      }
      this.setLoadingTrades(true);
      let promise = this.externalUser
        ? Repository.filteredTradesOfExternalUser(
            filter,
            this.$store.state.book.pageable
          )
        : Repository.filteredTradesOfUser(
            filter,
            this.$store.state.book.pageable
          );
      await promise
        .then(async (response) => {
          let trades = response.data.content;
          if (
            trades.length > 0 ||
            (trades.length === 0 && this.$store.state.book.filterMade)
          ) {
            this.tradeTimeAvg = 0;
            trades.forEach((item) => {
              item.mood = item.mood[0] + item.mood.slice(1).toLowerCase();
              item.side = item.side[0] + item.side.slice(1).toLowerCase();
              item.tradeTime = this.convertDuration(item.tradeTime);
              item.openTime = new Date(item.openTime);
              item.closeTime = new Date(item.closeTime);
              //item.openTime = this.convertUTCDateToLocalDate(new Date(item.openTime));
            });
            this.setTrades(response.data.content);
            let stats = {
              tradeTimeAvg: this.secondsToHHMMSS(
                this.tradeTimeAvg / trades.length
              ),
            };
            if (trades.length > 0) this.setStats(stats);
            this.setNoTrades(false);
            this.setTradesPageInfo({
              totalElements: response.data.totalElements,
              totalPages: response.data.totalPages,
            });
          } else {
            promise = this.externalUser
              ? Repository.allTradesOfExternalUser(
                  this.externalUserId,
                  this.$store.state.book.pageable
                )
              : Repository.allTradesOfUser(this.$store.state.book.pageable);
            await promise.then((response) => {
              let trades = response.data.content;
              this.tradeTimeAvg = 0;
              trades.forEach((item) => {
                item.mood = item.mood[0] + item.mood.slice(1).toLowerCase();
                item.side = item.side[0] + item.side.slice(1).toLowerCase();
                item.tradeTime = this.convertDuration(item.tradeTime);
                item.openTime = new Date(item.openTime);
                item.closeTime = new Date(item.closeTime);
                //item.openTime = this.convertUTCDateToLocalDate(new Date(item.openTime));
              });
              this.setTrades(response.data.content);
              let stats = {
                tradeTimeAvg: this.secondsToHHMMSS(
                  this.tradeTimeAvg / trades.length
                ),
              };
              if (trades.length > 0) {
                this.setStats(stats);
                this.setNoTrades(false);
              } else this.setNoTrades(true);
              this.setTradesPageInfo({
                totalElements: response.data.totalElements,
                totalPages: response.data.totalPages,
              });
            });
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(error.message);
        });
    },
    convertDuration(isoFormat) {
      let iso8601DurationRegex =
        /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/;
      let matches = isoFormat.match(iso8601DurationRegex);

      // Sum to the trade time average
      this.tradeTimeAvg +=
        (matches[8] === undefined ? 0 : matches[8] * 1) +
        (matches[7] === undefined ? 0 : matches[7] * 60) +
        (matches[6] === undefined ? 0 : matches[6] * 3600) +
        (matches[5] === undefined ? 0 : matches[5] * 86400) +
        (matches[4] === undefined ? 0 : matches[4] * 604800);

      return {
        sign: matches[1] === undefined ? "+" : "-",
        years: matches[2] === undefined ? 0 : matches[2],
        months: matches[3] === undefined ? 0 : matches[3],
        weeks: matches[4] === undefined ? 0 : matches[4],
        days: matches[5] === undefined ? 0 : matches[5],
        hours: matches[6] === undefined ? 0 : matches[6],
        minutes: matches[7] === undefined ? 0 : matches[7],
        seconds: matches[8] === undefined ? 0 : matches[8],
      };
    },
    convertUTCDateToLocalDate(date) {
      let newDate = new Date(
        date.getTime() + date.getTimezoneOffset() * 60 * 1000
      );
      let offset = date.getTimezoneOffset() / 60;
      let hours = date.getHours();
      newDate.setHours(hours - offset);
      return newDate;
    },
    secondsToHHMMSS(secondsToConvert) {
      let sec_num = parseInt(secondsToConvert, 10); // don't forget the second param
      let hours = Math.floor(sec_num / 3600);
      let minutes = Math.floor((sec_num - hours * 3600) / 60);
      let seconds = sec_num - hours * 3600 - minutes * 60;

      if (hours < 10) hours = "0" + hours;
      if (minutes < 10) minutes = "0" + minutes;
      if (seconds < 10) seconds = "0" + seconds;

      return `${hours}h ${minutes}m ${seconds}s`;
    },
    onItemChange() {
      this.filterChanged = true;
    },
  },
};
</script>

<style scoped></style>
